import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, todayIso } from '../../utils/formatters.js';
import { convertCents } from '../../utils/fx.js';
import { isPoLinkedPurchase } from './InvoiceMatchCard.jsx';

export function PayInvoiceModal({ invoice, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState('0');
  const [paidDate, setPaidDate] = useState(todayIso());
  const [force, setForce] = useState(false);
  const [paymentRate, setPaymentRate] = useState('');
  const [error, setError] = useState(null);

  const isFx = Boolean(invoice && invoice.currency && invoice.currency !== 'CHF');

  const outstanding = invoice
    ? Math.max(0, Number(invoice.totalAmount) - Number(invoice.paidAmount) - Number(invoice.creditedAmount || 0))
    : 0;

  useEffect(() => {
    if (invoice) {
      setAmount(outstanding.toFixed(2));
      setPaidDate(todayIso());
      setForce(false);
      setPaymentRate('');
      setError(null);
    }
  }, [invoice, outstanding]);

  // Prefill the payment rate from the latest stored rate; manual input is
  // never overwritten (only fills while the field is still '').
  const latestRate = useQuery({
    queryKey: ['finance', 'exchange-rate-latest', invoice?.currency],
    queryFn: () =>
      financeApi
        .listExchangeRates({ currency: invoice.currency, limit: 1 })
        .then((r) => (r.items || [])[0] || null),
    enabled: Boolean(invoice) && isFx,
  });
  useEffect(() => {
    const fetched = latestRate.data?.rate;
    if (!fetched) {
      return;
    }
    setPaymentRate((prev) => (prev === '' ? String(Number(fetched)) : prev));
    // invoice?.id re-fires the prefill when the dialog opens for another
    // invoice - the cached query data keeps its identity (structural sharing),
    // so latestRate.data alone would not trigger after the field reset.
  }, [latestRate.data, invoice?.id]);

  const matchable = isPoLinkedPurchase(invoice);
  const match = useQuery({
    queryKey: ['finance', 'invoices', invoice?.id, 'match'],
    queryFn: () => financeApi.invoiceMatch(invoice.id),
    enabled: matchable,
  });
  const matchStatus = matchable ? match.data?.status : null;

  const pay = useMutation({
    mutationFn: ({ id, body }) => financeApi.payInvoice(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    // The Pay button lives in the Modal footer outside the <form>, so the rate
    // input's `required` never fires - guard the FX rate here instead.
    if (isFx && !(Number(paymentRate) > 0)) {
      setError(t('invoice.fx.noRate', { currency: invoice.currency }));
      return;
    }
    const body = { amount, paidDate };
    if (force) body.force = true;
    if (isFx) body.paymentRate = Number(paymentRate);
    pay.mutate({ id: invoice.id, body });
  }

  // CHF preview: what the bank pays at the entered rate vs. what the invoice
  // was booked at - the difference is the FX gain/loss the backend will post.
  const amountCents = Math.round(Number(amount || 0) * 100);
  const bankCents = convertCents(amountCents, Number(paymentRate));
  const settleCents = invoice ? convertCents(amountCents, Number(invoice.exchangeRate)) : 0;
  const diffCents = bankCents - settleCents;
  const showFxPreview = isFx && Number(paymentRate) > 0;

  return (
    <Modal
      open={Boolean(invoice)}
      onClose={onClose}
      title={t('finance.invoices.pay.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={pay.isPending}>
            {pay.isPending ? `${t('common.save')}…` : t('finance.invoices.action.pay')}
          </Button>
        </>
      }
    >
      {invoice && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          <p className="muted text-sm" style={{ margin: 0 }}>
            {invoice.invoiceNumber} - {t('finance.invoices.pay.outstanding')}:{' '}
            <strong>{formatCurrency(outstanding, invoice.currency)}</strong>
          </p>
          <div className="field-row">
            <label className="field">
              <span className="field-label">{t('finance.invoices.pay.amount')}</span>
              <input
                className="input"
                type="number"
                min="0.01"
                max={outstanding}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </label>
            <label className="field">
              <span className="field-label">{t('finance.invoices.pay.date')}</span>
              <input
                className="input"
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                required
              />
            </label>
          </div>

          {isFx && (
            <label className="field">
              <span className="field-label">{t('invoice.fx.paymentRate')}</span>
              <input
                className="input"
                type="number"
                min="0.000001"
                step="0.000001"
                value={paymentRate}
                onChange={(e) => setPaymentRate(e.target.value)}
                required
              />
            </label>
          )}
          {showFxPreview && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }} className="muted text-sm">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{t('invoice.fx.bankAmount')}</span>
                {/* Converted preview is mathematically CHF - never the display currency. */}
                <span className="mono">{formatCurrency(bankCents / 100, 'CHF')}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{t('invoice.fx.diff')}</span>
                <span className="mono">{`${diffCents >= 0 ? '+' : '-'}${formatCurrency(Math.abs(diffCents) / 100, 'CHF')}`}</span>
              </div>
            </div>
          )}

          {matchStatus === 'OVER_BILLED' && (
            <div className="alert danger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <span>
                {t('match.overWarning', {
                  diff: formatCurrency(match.data?.difference, invoice.currency),
                })}
              </span>
              <label style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                />
                <span>{t('match.forceLabel')}</span>
              </label>
            </div>
          )}
          {matchStatus === 'UNDER_BILLED' && (
            <div className="alert">{t('match.underHint')}</div>
          )}
          {matchStatus === 'NO_RECEIPT' && (
            <div className="alert">{t('match.noReceiptHint')}</div>
          )}
          {matchStatus === 'MATCHED' && (
            <p className="muted text-sm" style={{ margin: 0 }}>{t('match.okHint')}</p>
          )}

          {error && <div className="alert danger">{error}</div>}
        </form>
      )}
    </Modal>
  );
}
